import React, { useMemo } from "react";

import PageHead from "../../components/PageHead";
import * as api from "./api";
import OfferPicker from "./OfferPicker";
import { Async, Metric, Panel } from "./panels";
import { useEndpoint } from "./useOffer";

/**
 * Workstation · Traffic Channels (§4, tab 6 of 8).
 *
 * `design/system/audits/workstation-tabs.md` §6
 * ==============================================
 * There is no channel-mix aggregation endpoint — the audit is explicit that
 * writing one would be new business-logic-free composition, Founder-parked
 * (§6 exception), not overnight work. What exists, both live and both
 * offer-reachable, is composed here client-side:
 *
 *   GET /api/campaigns   campaigns.py:508 — each row carries `channel` and
 *       `offer_id` (both required, `models.py:352,355`). Account-wide: the
 *       route has no `offer_id` filter, so `api.js` narrows to the selected
 *       offer client-side. Until #220 this file called
 *       `GET /api/offers/{offer_id}/campaigns`, which is not mounted.
 *   GET /api/performance/snapshots?offer_id=   performance.py:552 — each row
 *       carries `campaign_id`, `traffic_source`, `affiliate_network`
 *       (`models.py:439-440`), populated by the RedTrack pull
 *       (`services/redtrack/client.py:224`, `:549`)
 *
 * Joining snapshots to campaigns by `campaign_id`, then grouping by
 * `Campaign.channel`, is the channel mix. This file does exactly that and no
 * more — it does not invent an aggregation the backend does not have.
 *
 * TWO TRAPS THE AUDIT NAMES, AVOIDED HERE
 * ========================================
 * `/api/networks` is the affiliate/payout network registry (ClickBank etc.),
 * not traffic channels — not used. `/api/performance/segments` returns only
 * `placement` and `region`, which is within-platform (Feed/Reels) rather
 * than a traffic channel, and belongs on Research & Audience, not here.
 */

function composeChannels(campaigns, snapshots) {
  const byCampaign = new Map();
  for (const c of campaigns) {
    byCampaign.set(c.campaign_id, c.channel || "unspecified");
  }
  const byChannel = new Map();
  for (const s of snapshots) {
    const channel = byCampaign.get(s.campaign_id) || "unspecified";
    const row = byChannel.get(channel) || {
      channel,
      spend: 0,
      revenue: 0,
      conversions: 0,
      trafficSources: new Set(),
      affiliateNetworks: new Set(),
    };
    row.spend += Number(s.spend) || 0;
    row.revenue += Number(s.revenue) || 0;
    row.conversions += Number(s.conversions) || 0;
    if (s.traffic_source) row.trafficSources.add(s.traffic_source);
    if (s.affiliate_network) row.affiliateNetworks.add(s.affiliate_network);
    byChannel.set(channel, row);
  }
  // Channels that exist on campaigns but have no snapshot rows yet still
  // belong on the mix — a channel with zero spend recorded is a real fact,
  // not an absence of the channel.
  for (const channel of byCampaign.values()) {
    if (!byChannel.has(channel)) {
      byChannel.set(channel, {
        channel,
        spend: 0,
        revenue: 0,
        conversions: 0,
        trafficSources: new Set(),
        affiliateNetworks: new Set(),
      });
    }
  }
  return Array.from(byChannel.values()).sort((a, b) => b.spend - a.spend);
}

function ChannelMix({ offerId }) {
  const campaigns = useEndpoint(() => api.getOfferCampaigns(offerId), [offerId], {
    skip: !offerId,
  });
  const snapshots = useEndpoint(() => api.getSnapshots(offerId), [offerId], {
    skip: !offerId,
  });

  const rows = useMemo(() => {
    if (campaigns.state !== "ready" || snapshots.state !== "ready") return null;
    // A bare array — `GET /api/campaigns` returns `List[Campaign]`. The old
    // `campaigns.data?.campaigns` read a key no response has ever had, so even
    // once the URL was right it would still have composed from nothing (#220).
    const c = Array.isArray(campaigns.data) ? campaigns.data : [];
    const s = Array.isArray(snapshots.data) ? snapshots.data : [];
    return composeChannels(c, s);
  }, [campaigns.state, campaigns.data, snapshots.state, snapshots.data]);

  return (
    <Panel
      title="Channel mix"
      subtitle="Campaigns grouped by channel, joined to their snapshot spend/revenue. Composed client-side — no aggregation endpoint exists for this."
    >
      {campaigns.state === "loading" || snapshots.state === "loading" ? (
        <div className="ws-loading" role="status" aria-live="polite">
          <span className="skeleton ws-skeleton-line" />
          <span className="skeleton ws-skeleton-line" />
        </div>
      ) : campaigns.state === "error" ? (
        <div className="state state-error ws-state">Could not read campaigns: {campaigns.error}</div>
      ) : snapshots.state === "error" ? (
        <div className="state state-error ws-state">Could not read snapshots: {snapshots.error}</div>
      ) : !rows || rows.length === 0 ? (
        <div className="state ws-state">No campaigns for this offer yet, so there is no channel mix to show.</div>
      ) : (
        <div className="ws-table-scroll">
          <table className="tbl ws-table">
            <thead>
              <tr>
                <th>Channel</th>
                <th>Spend</th>
                <th>Revenue</th>
                <th>Conversions</th>
                <th>Traffic source(s)</th>
                <th>Affiliate network(s)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.channel}>
                  <td>{r.channel}</td>
                  <td><Metric value={r.spend} format="currency" /></td>
                  <td><Metric value={r.revenue} format="currency" /></td>
                  <td><Metric value={r.conversions} /></td>
                  <td>{r.trafficSources.size ? Array.from(r.trafficSources).join(", ") : "—"}</td>
                  <td>{r.affiliateNetworks.size ? Array.from(r.affiliateNetworks).join(", ") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function DesignatedChannel({ offer }) {
  return (
    <Panel title="Designated channel" subtitle="The single channel this offer is set up on, per its record.">
      {offer?.channel ? (
        <p className="ws-understanding-lead" style={{ margin: 0 }}>{offer.channel}</p>
      ) : (
        <div className="state ws-state">No channel set on this offer's record.</div>
      )}
    </Panel>
  );
}

export default function TrafficChannels({ selection }) {
  const { offers, selected, selectedId, select, error } = selection;

  return (
    <>
      <PageHead
        title="Traffic Channels"
        subtitle={
          selected
            ? `Where ${selected.name || "this offer"}'s traffic comes from.`
            : "Where the traffic comes from."
        }
      />

      <OfferPicker
        offers={offers}
        selectedId={selectedId}
        onSelect={select}
        error={error}
      />

      {!selectedId ? (
        <div className="state ws-state" role="status">
          Select an offer to see its traffic channels.
        </div>
      ) : (
        <div className="ws-analytics-grid">
          <DesignatedChannel offer={selected} />
          <ChannelMix offerId={selectedId} />
        </div>
      )}
    </>
  );
}
